<template>
  <div class="video-room-container">
    <template v-if="joined">
      <whiteboard
        v-show="isDisplayingWhiteboard"
        ref="whiteboard"
        @onClickScreenShare="toggleScreenShare"
        @onClearWhiteboard="onClearWhiteboard"
      />
      <peers-container
        :viewMode="peersContainerViewMode"
        :guest="!!guestToken"
        @onClickMyRaisedHands="toggleRaisedHands"
        @onClickMyMicState="toggleMic"
      />
      <peers-info-pain
        :enabledPain="peersInfoPain"
        :videoRoom="videoRoom"
        :cafeRoom="!guestToken && videoRoomMaster.roomType === 'cafeRoom'"
        :guest="!!guestToken"
        @onClickClose="peersInfoPain = false"
      />
      <chat-pain
        :enabledPain="chatPain"
        @onClickSend="onClickSendChatMessage"
        @onClickClose="chatPain = false"
      />
      <room-control
        :videoRoom="videoRoom"
        :cafeRoom="!guestToken && videoRoomMaster.roomType === 'cafeRoom'"
        :isOwner="!guestToken && videoRoom.roomOwnerId == signedInUser.id"
        :peersInfoState="peersInfoPain"
        :whiteboardState="isDisplayingWhiteboard"
        :screenShareState="screenSharing"
        :chatState="chatPain"
        :myWebcamState="myPeer.webcamState"
        :myMicState="myPeer.micState"
        :myRaisedHandsState="myPeer.raisedHandsState"
        :newChatMessagesState="newChatMessagesCount"
        :raisedHandsPeersState="raisedHandsPeersCount"
        :addMemberModalState="addMemberModal"
        @onClickConfig="onClickConfig"
        @onClickPeersInfo="togglePeersInfoPain"
        @onClickWhiteboard="toggleWhiteboard"
        @onClickScreenShare="toggleScreenShare"
        @onClickChat="toggleChatPain"
        @onClickWebcam="toggleWebcam"
        @onClickMic="toggleMic"
        @onClickRaisedHands="toggleRaisedHands"
        @onClickAddMember="addMemberModal = true"
        @onClickExit="exitRoomModal = true"
      />
    </template>
    <div class="toast-notifications-wrapper">
      <proa-toast
        v-if="displayToast"
        @onClickToast="closeToast"
        @onTimeout="closeToast"
      >
        <template slot="toast">
          <span class="toast-notification-content">
            会議終了まで、残り<span class="remain-meeting-time">{{ remainMeetingTime }}</span>分です
          </span>
        </template>
      </proa-toast>
    </div>
    <proa-modal
      :modal="enterRoomPasswordModal"
      :title="$t('models.videoRoom.attributes.roomPassword')"
      @close="enterRoomPasswordModal = false"
      hideCloseButton
      noCloseOnBackdrop
    >
      <div class="text-pre-wrap mb-3">{{ $t('pages.room.enterRoomPasswordModalCaption') }}</div>
      <proa-input-group :label="$t('models.videoRoom.attributes.roomPassword')">
        <template slot="input">
          <proa-input
            v-model="roomPassword"
            type="text"
            dark
          />
        </template>
      </proa-input-group>
      <template slot="footer">
        <proa-button
          @click.native="enterRoomPasswordModal = false; userConfigurationModal = true"
          :disabled="!roomPassword"
          class="default-button-width mr-2"
        >
          {{ $t('common.next') }}
        </proa-button>
      </template>
    </proa-modal>
    <proa-modal
      :modal="userConfigurationModal"
      :title="$t('pages.room.userConfigurationModal.title')"
      @close="userConfigurationModal = false"
      hideCloseButton
      noCloseOnBackdrop
    >
      <proa-input-group :label="$t('models.user.attributes.displayName')">
        <template slot="input">
          <proa-input
            v-model="displayName"
            :disabled="enterRoomRequesting"
            type="text"
            dark
          />
        </template>
      </proa-input-group>
      <proa-input-group :label="$t('pages.room.webcam')">
        <template slot="input">
          <proa-radio-button
            :checked="defaultEnableWebcam"
            :disabled="enterRoomRequesting"
            @change="defaultEnableWebcam = true"
            class="mr-3"
          >
            <span>{{ $t('pages.room.webcamStatus.on') }}</span>
          </proa-radio-button>
          <proa-radio-button
            :checked="!defaultEnableWebcam"
            :disabled="enterRoomRequesting"
            @change="defaultEnableWebcam = false"
          >
            <span>{{ $t('pages.room.webcamStatus.off') }}</span>
          </proa-radio-button>
        </template>
      </proa-input-group>
      <div class="media-select">
        <proa-select
          v-model="mediaVideoInputId"
          :options="mediaInputOptions('videoinput')"
        />
      </div>
      <proa-input-group :label="$t('pages.room.mic')">
        <template slot="input">
          <proa-radio-button
            :checked="defaultEnableMic"
            :disabled="enterRoomRequesting"
            @change="defaultEnableMic = true"
            class="mr-3"
          >
            <span>{{ $t('pages.room.micStatus.on') }}</span>
          </proa-radio-button>
          <proa-radio-button
            :checked="!defaultEnableMic"
            :disabled="enterRoomRequesting"
            @change="defaultEnableMic = false"
          >
            <span>{{ $t('pages.room.micStatus.off') }}</span>
          </proa-radio-button>
        </template>
      </proa-input-group>
      <div class="media-select">
        <proa-select
          v-model="mediaAudioInputId"
          :options="mediaInputOptions('audioinput')"
        />
      </div>
      <template slot="footer">
        <proa-button
          @click.native="onClickEnterRoom"
          :disabled="!displayName"
          :loading="enterRoomRequesting"
          class="default-button-width mr-2"
        >
          {{ $t('common.ok') }}
        </proa-button>
      </template>
    </proa-modal>
    <proa-modal
      :modal="updateConfigurationModal"
      :title="$t('pages.room.userConfigurationModal.title')"
      @close="updateConfigurationModal = false"
    >
      <proa-input-group :label="$t('pages.room.webcam')"/>
      <div class="media-select">
        <proa-select
          v-model="tmpMediaVideoInputId"
          :disabled="!myPeer.webcamState"
          :options="mediaInputOptions('videoinput')"
        />
      </div>
      <proa-input-group :label="$t('pages.room.mic')"/>
      <div class="media-select">
        <proa-select
          v-model="tmpMediaAudioInputId"
          :disabled="!myPeer.micState"
          :options="mediaInputOptions('audioinput')"
        />
      </div>
      <template slot="footer">
        <proa-button
          @click.native="onClickUpdateConfig"
          :disabled="tmpMediaVideoInputId == mediaVideoInputId && tmpMediaAudioInputId == mediaAudioInputId"
          class="default-button-width mr-2"
        >
          {{ $t('common.ok') }}
        </proa-button>
      </template>
    </proa-modal>
    <proa-modal
      v-if="videoRoom"
      :modal="waitingRoomModal"
      :title="$t('pages.room.waitingRoomModal.title')"
      class="waiting-room-modal"
      hideCloseButton
      noCloseOnBackdrop
      size="xlg"
    >
      <div class="waiting-room-wrapper h-100 d-flex flex-column align-items-center">
        <div class="waiting-time-wrapper">
          <span class="title">{{ $t('pages.room.waitingRoomModal.body') }}</span>
          <span>{{ waitingTime }}</span>
        </div>
        <div class="peers-container">
          <peer-view
            :avatar="!!guestToken ? null : signedInUser.meta.avatar"
            :displayName="myPeer.displayName"
            :videoStream="enableWebCam"
            :audioStream="enableMic"
            :micState="myPeer.micState"
            myself
          />
          <div class="user-setting">
            <table class="room-information-table">
              <tbody>
                <tr>
                  <th>{{ $t('models.videoRoom.reservationAt') }}</th>
                  <td>
                    <span class="mr-2">{{ videoRoom.startAt | moment('YYYY年MM月DD日（ddd）') }}</span>
                    <span>{{ videoRoom.startAt | moment('HH:mm') }}〜{{ videoRoom.endAt | moment('HH:mm') }}</span>
                  </td>
                </tr>
                <tr>
                  <th>{{ $t('models.videoRoom.attributes.meetingTitle') }}</th>
                  <td>{{ videoRoom.meetingTitle }}</td>
                </tr>
              </tbody>
            </table>
            <div class="user-setting-heading">
              <proa-heading detail>
                {{ $t('common.deviceSetting') }}
              </proa-heading>
            </div>
            <proa-input-group :label="$t('pages.room.webcam')" />
            <div class="media-select">
              <proa-select
                v-model="mediaVideoInputId"
                :options="mediaInputOptions('videoinput')"
                @input="changeDevices({video: true})"
              />
            </div>
            <proa-input-group :label="$t('pages.room.mic')" />
            <div class="media-select">
              <proa-select
                v-model="mediaAudioInputId"
                :options="mediaInputOptions('audioinput')"
                @input="changeDevices({audio: true})"
              />
            </div>
          </div>
        </div>
      </div>
      <template slot="footer">
        <proa-button @click.native="exitRoom" class="default-button-width mr-2">
          {{ $t('pages.room.waitingRoomModal.exitRoom') }}
        </proa-button>
      </template>
    </proa-modal>
    <proa-modal
      :modal="exitRoomModal"
      :title="$t('pages.room.exitRoomModal.title')"
      @close="exitRoomModal = false"
    >
      <div>{{ $t('pages.room.exitRoomModal.message') }}</div>
      <template slot="footer">
        <proa-button
          @click.native="exitRoomModal = false"
          class="default-button-width mr-2"
        >
          {{ $t('common.cancel') }}
        </proa-button>
        <proa-button
          @click.native="exitRoom"
          class="default-button-width mr-2"
        >
          {{ $t('pages.room.exitRoomModal.ok') }}
        </proa-button>
      </template>
    </proa-modal>
    <proa-modal
      :modal="errorCode != null"
      :title="$t('pages.room.sfuErrorModal.title')"
      @close="exitRoom"
      hideCloseButton
      noCloseOnBackdrop
    >
      <div class="text-pre-wrap">{{ sfuErrorModalMessageByErrorCode }}</div>
      <template v-if="errorCode == 15" slot="footer">
        <proa-button @click.native="exitRoom" class="default-button-width mr-2">
          {{ $t('common.cancel') }}
        </proa-button>
        <proa-button @click.native="reEnterRoom" class="default-button-width">
          {{ $t('pages.room.sfuErrorModal.reEnter') }}
        </proa-button>
      </template>
    </proa-modal>
    <select-member-modal
      v-if="!guestToken && videoRoom"
      :modal="addMemberModal"
      :userIds="videoRoom.userIds"
      :upperLimit="videoRoomMaster.roomCapacity - videoRoom.userIds.length"
      type="add"
      :guestable="videoRoomMaster.roomType !== 'cafeRoom'"
      @close="addMemberModal = false"
      @confirm="addUsers"
    />
    <proa-modal
      :modal="addedMembersModalWithUserIds != null"
      :title="$t('pages.room.addedMembersModal.title')"
      @close="addedMembersModalWithUserIds = null"
    >
      <div v-for="user in addedUsers" :key="user.id" style="margin-bottom: 10px;">
        <proa-avatar :src="user.meta.avatar || require('@/assets/images/avatar_default.png')" size="sm"/>
        <span class="ml-2 pl-1">{{ user.meta.displayName }}</span>
      </div>
      <div v-for="guestUser in addedGuestUsers" :key="guestUser.id" style="margin-bottom: 10px;">
        <proa-avatar :src="require('@/assets/images/avatar_default.png')" size="sm" guest/>
        <span class="ml-2 pl-1">
          <span>{{ guestUser.lastName }} </span>
          <span class="text-secondary">{{ guestUser.firstName }}</span>
          <span> / {{ guestUser.email }}</span>
        </span>
      </div>
    </proa-modal>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import moment from 'moment'
import i18n from '@/i18n'
import WebRTCClient from '@/lib/mediasoup/WebRTCClient'

import User from '@/store/models/User'
import VideoRoomMaster from '@/store/models/VideoRoomMaster'
import VideoRoom from '@/store/models/VideoRoom'

import SelectMemberModal from '@/components/organisms/SelectMemberModal'
import RoomControl from '@/components/molecules/room/RoomControl'
import PeersContainer from '@/components/organisms/room/PeersContainer'
import PeerView from '@/components/molecules/room/PeerView'
import PeersInfoPain from '@/components/organisms/room/PeersInfoPain'
import ChatPain from '@/components/organisms/room/ChatPain'
import Whiteboard from '@/components/organisms/room/Whiteboard'

export default {
  name: 'IndexRoom',
  components: {
    SelectMemberModal,
    RoomControl,
    PeersContainer,
    PeerView,
    PeersInfoPain,
    ChatPain,
    Whiteboard
  },
  computed: {
    guestToken: function() { return this.$route.query.token },
    videoRoomMasterId: function() {
      return this.guestToken ?
             this.guestInvitedVideoRoom.videoRoomMasterId :
             this.$route.params.videoRoomMasterId
    },
    videoRoomMaster: function() { return VideoRoomMaster.find(this.videoRoomMasterId) },
    videoRoomId: function() {
      return this.guestToken ?
             this.guestInvitedVideoRoom.id :
             this.$route.params.id
    },
    videoRoom: function() {
      return this.guestToken ?
             this.guestInvitedVideoRoom :
             VideoRoom.find(this.videoRoomId)
    },
    addedUsers: function() { return User.query().whereIdIn(this.addedMembersModalWithUserIds).get() },
    mediaInputOptions: function() {
      return function(kind) {
        let selectedId
        if (kind === 'audioinput') {
          selectedId = this.mediaAudioInputId
        } else if (kind === 'videoinput') {
          selectedId = this.mediaVideoInputId
        }
        return this.mediaDevices.filter(device => {
          return device.deviceId !== 'default' && device.kind === kind
        }).map(device => {
          return {
            label: device.label ? device.label : i18n.tc('pages.room.unknownDevice'),
            value: device.deviceId,
            selected: device.deviceId == selectedId
          }
        })
      }
    },
    peersContainerViewMode: function() {
      if (this.isDisplayingWhiteboard) {
        return 'whiteboard'
      } else if (this.peersInfoPain || this.chatPain) {
        return 'side-pain'
      } else {
        return 'fluid'
      }
    },
    sfuErrorModalMessageByErrorCode: function(){
      let errorMessage
      switch (this.errorCode) {
        case 3: {
          errorMessage = i18n.tc('pages.room.sfuErrorModal.messages.invalidPassword')
          break
        }
        case 11: {
          errorMessage = i18n.tc('pages.room.sfuErrorModal.messages.connectionFailedAPIServer')
          break
        }
        case 14: {
          errorMessage = i18n.tc('pages.room.sfuErrorModal.messages.unauthorized')
          break
        }
        case 15: {
          errorMessage = i18n.tc('pages.room.sfuErrorModal.messages.duplicateEntry')
          break
        }
        case 16: {
          errorMessage = i18n.tc('pages.room.sfuErrorModal.messages.invalidRoomInformation')
          break
        }
        default: {
          errorMessage = i18n.tc('pages.room.sfuErrorModal.messages.other')
        }
      }
      return errorMessage
    },
    ...mapGetters({
      httpError: 'httpError',
      currentOrganizationId: 'auth/currentOrganizationId',
      signedInUser: 'auth/signedInUser',
      myPeer: 'roomPeers/myPeer',
      frontPeerIds: 'roomPeers/frontPeerIds',
      isDisplayingWhiteboard: 'roomWhiteboard/displaying',
      screenSharing: 'roomWhiteboard/screenSharing',
      sendWhiteboardData: 'roomWhiteboard/sendData',
      newChatMessagesCount: 'roomChatMessages/newChatMessagesCount',
      raisedHandsPeersCount: 'roomPeers/raisedHandsPeersCount',
      guestInvitedVideoRoom: 'inviteAuth/invitedVideoRoom'
    })
  },
  data() {
    return {
      enterRoomPasswordModal: false,
      userConfigurationModal: false,
      updateConfigurationModal: false,
      waitingRoomModal: false,
      addMemberModal: false,
      addedMembersModalWithUserIds: null,
      addedGuestUsers: [],
      exitRoomModal: false,
      errorCode: null,
      enterRoomRequesting: false,
      peersInfoPain: false,
      chatPain: false,
      roomPassword: '',
      displayName: '',
      defaultEnableMic: true,
      defaultEnableWebcam: true,
      mediaDevices: [],
      tmpMediaAudioInputId: null,
      tmpMediaVideoInputId: null,
      mediaAudioInputId: null,
      mediaVideoInputId: null,
      webRTCClient: null,
      waitingRoomInterval: null,
      waitingTime: null,
      enabledEnterRoom: false,
      enableMic: null,
      enableWebCam: null,
      closed: false,
      joined: false,
      displayToast: false,
      remainMeetingTime: ''
    }
  },
  methods: {
    async onClickConfig() {
      this.mediaDevices = await navigator.mediaDevices.enumerateDevices()
      this.tmpMediaAudioInputId = this.mediaAudioInputId
      this.tmpMediaVideoInputId = this.mediaVideoInputId
      this.updateConfigurationModal = true
    },
    onClickEnterRoom: function() {
      this.enterRoomRequesting = true
      this.initWebRTCClient()
    },
    onClickSendChatMessage: function(message) {
      this.webRTCClient.sendChatMessage(message)
    },
    displayToastRemainTime: function (remainTime) {
      this.remainMeetingTime = remainTime
      this.displayToast = true
    },
    closeToast: function () {
      this.displayToast = false
    },
    async onClickUpdateConfig() {
      if (this.mediaAudioInputId != this.tmpMediaAudioInputId) {
        this.mediaAudioInputId = this.tmpMediaAudioInputId
        await this.webRTCClient.updateMic(this.mediaAudioInputId)
      }

      if (this.mediaVideoInputId != this.tmpMediaVideoInputId) {
        this.mediaVideoInputId = this.tmpMediaVideoInputId
        await this.webRTCClient.updateWebcam(this.mediaVideoInputId)
      }
      this.updateConfigurationModal = false
    },
    initWebRTCClient: function() {
      const initWebRTCClientParams = {
        videoRoomId: this.videoRoomId,
        videoRoomMemberId: this.videoRoom.myMemberId,
        displayName: this.displayName,
        roomPassword: this.roomPassword,
        mediaVideoInputId: this.mediaVideoInputId,
        mediaAudioInputId: this.mediaAudioInputId
      }
      if (this.guestToken) {
        initWebRTCClientParams.userId = this.guestInvitedVideoRoom.myMember.userId
        initWebRTCClientParams.organizationId = this.guestInvitedVideoRoom.organizationId
        initWebRTCClientParams.guestToken = this.guestToken
      } else {
        initWebRTCClientParams.userId = this.signedInUser.id
        initWebRTCClientParams.organizationId = this.currentOrganizationId
      }

      this.webRTCClient = new WebRTCClient(initWebRTCClientParams)

      this.webRTCClient.on('enterRoomResult', result => {
        this.enterRoomRequesting = this.userConfigurationModal = false
        if (result.entered) {
          this.webRTCClient.joinRoom()
        } else {
          console.error(result.errorCode)
          this.errorCode = result.errorCode || 'other'
        }
      })

      this.webRTCClient.on('joinRoomResult', joined => {
        this.joined = joined

        if (this.defaultEnableMic) this.webRTCClient.enableMic()
        if (this.defaultEnableWebcam) this.webRTCClient.enableWebcam()

        this.webRTCClient.enableRoomControlDataProducer()
        this.webRTCClient.enableChatDataProducer()
        this.webRTCClient.enableWhiteboardDataProducer()

        if (!this.joined && !this.errorCode) {
          this.errorCode = 'other'
        }
      })

      this.webRTCClient.on('closeProtoo', () => {
        if (!this.closed && !this.errorCode)  {
          this.errorCode = 'other'
        }
      })

      this.webRTCClient.on('receivedChatMessage', () => {
        if (this.chatPain) return
        this.setNewChatMessagesCount(this.newChatMessagesCount + 1)
      })

      this.webRTCClient.on('displayToastRemainTime', (remainTime) => {
        this.displayToastRemainTime(remainTime)
      })

      this.webRTCClient.on('endMeeting', () => {
        this.exitRoom()
      })

      // NOTE: ここはPeersのstateで管理するので保留
      // this.webRTCClient.on('raisedHands', (raised) => {
      //   if (this.peersInfoPain) return
      // })
    },
    toggleRaisedHands: function() {
      this.webRTCClient.sendRaisedHands(!this.myPeer.raisedHandsState)
    },
    toggleMic: function() {
      if (this.myPeer.micState) {
        this.webRTCClient.disableMic()
      } else {
        this.webRTCClient.enableMic()
      }
    },
    toggleWebcam: function() {
      if (this.myPeer.webcamState) {
        this.webRTCClient.disableWebcam()
      } else {
        this.webRTCClient.enableWebcam()
      }
    },
    toggleScreenShare: function() {
      switch (this.screenSharing) {
        case 0: {
          this.webRTCClient.enableScreenShare()
          break
        }
        case 1: {
          this.webRTCClient.disableScreenShare()
          break
        }
      }
    },
    async togglePeersInfoPain() {
      const decide = () => {
        this.peersInfoPain = !this.peersInfoPain
        if (this.isDisplayingWhiteboard) this.$refs.whiteboard.adjustMagnification()
      }
      if (this.chatPain) {
        this.chatPain = false
        setTimeout(decide, 400)
      } else {
        decide()
      }
    },
    toggleChatPain: function() {
      const decide = () => {
        this.chatPain = !this.chatPain
        if (this.isDisplayingWhiteboard) this.$refs.whiteboard.adjustMagnification()
        if (this.chatPain) {
          this.setNewChatMessagesCount(0)
        }
      }
      if (this.peersInfoPain) {
        this.peersInfoPain = false
        setTimeout(decide, 400)
      } else {
        decide()
      }
    },
    async addUsers({ userIds, guestUsers }) {
      this.addMemberModal = false
      if (!userIds.length && !guestUsers.length) return

      await this.apiPutVideoRoom({
        videoRoomMasterId: this.videoRoomMasterId,
        videoRoomId: this.videoRoomId,
        attributes: {
          userIds: this.videoRoom.userIds.concat(userIds)
        }
      })

      for(const guestUser of guestUsers) {
        await this.apiPostVideoRoomMember({
          videoRoomMasterId: this.videoRoomMasterId,
          videoRoomId: this.videoRoomId,
          attributes: guestUser
        })
      }

      if (!this.httpError) {
        // NOTE: guestUsersを追加した場合userIdsを同期させるためにVideoRoomを再取得
        if (guestUsers.length) {
          await this.apiGetVideoRoom({
            videoRoomMasterId: this.videoRoomMasterId,
            id: this.videoRoomId
          })
        }
        this.addedGuestUsers = guestUsers
        this.addedMembersModalWithUserIds = userIds
      }
    },
    onClearWhiteboard: function() {
      this.webRTCClient.sendWhiteboardMessage(JSON.stringify({
        action: 'clear'
      }))
    },
    exitRoom: function() {
      // TODO: ゲストの退室
      if (this.guestToken && this.guestInvitedVideoRoom) {
        this.$router.push({ name: 'guestMeetingThanks', params: { guestToken: this.guestToken } })
      } else if (this.videoRoomMaster.roomType === 'cafeRoom') {
        this.$router.push({ name: 'indexMeetingReservations', params: { id: this.videoRoomMasterId } })
      } else {
        this.$router.push({ name: 'showMeetingReservations', params: { videoRoomMasterId: this.videoRoomMasterId, id: this.videoRoomId } })
      }
    },
    beforeunloadOnWindow: function(e) {
      e.preventDefault()
      e.returnValue = i18n.tc('pages.room.beforeunloadMessage')
    },
    unloadOnWindow: function() {
      this.close()
    },
    close: function() {
      if (this.closed) return
      this.closed = true
      if (this.waitingRoomInterval) clearInterval(this.waitingRoomInterval)
      this.webRTCClient?.close()
      this.resetRoomChatMessages()
      this.resetRoomPeers()
      this.resetRoomWhiteboard()
    },
    closeEnabledDevices: function ({ audio, video }) {
      if (audio) {
        this.enableMic.getAudioTracks().forEach((track) => {
          track.stop()
        })
      }
      if (video) {
        this.enableWebCam.getVideoTracks().forEach((track) => {
          track.stop()
        })
      }
    },
    async reEnterRoom() {
      if (this.guestToken && this.guestInvitedVideoRoom) {
        await this.apiPutGuestForceExit(this.guestToken)
      } else {
        await this.apiPutVideoRoomMember({
          videoRoomMasterId: this.videoRoomMasterId,
          videoRoomId: this.videoRoomId,
          id: this.videoRoom.myMemberId,
          attributes: {
            isJoined: false
          }
        })
      }
      window.removeEventListener('beforeunload', this.beforeunloadOnWindow)
      this.$router.go({ path: this.$route.path, force: true })
    },
    async settingMic() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: this.mediaAudioInputId
          }
        })
        return stream
      } catch (e) {
        console.error(`settingMic() | failed:${e}`)
      }
    },
    async settingWebCam() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: this.mediaVideoInputId,
            frameRate: { ideal: 10, max: 30 }
          }
        })
        return stream
      } catch (e) {
        console.error(`settingWebCam() | failed:${e}`)
      }
    },
    async changeDevices({ audio, video }) {
      if (audio) {
        this.closeEnabledDevices({ audio: audio })
        this.enableMic = await this.settingMic()
      }
      if (video) {
        this.closeEnabledDevices({ video: video })
        this.enableWebCam = await this.settingWebCam()
      }
    },
    ...mapMutations({
      resetRoomChatMessages: 'roomChatMessages/resetModule',
      resetRoomPeers: 'roomPeers/resetModule',
      resetRoomWhiteboard: 'roomWhiteboard/resetModule'
    }),
    ...mapActions({
      apiGetVideoRoomMaster: 'entities/videoRoomMasters/show',
      apiGetVideoRoom: 'entities/videoRooms/show',
      apiPostVideoRoomMember: 'videoRoomMembers/create',
      apiPutGuestForceExit: 'inviteAuth/guestForceExit',
      apiPutUserNotificationToRead: 'entities/userNotifications/updateToRead',
      apiPutVideoRoomMember: 'videoRoomMembers/update',
      apiPutVideoRoom: 'entities/videoRooms/update',
      toggleWhiteboard: 'roomWhiteboard/toggleWhiteboard',
      setNewChatMessagesCount: 'roomChatMessages/setNewChatMessagesCount'
    })
  },
  async created() {
    // NOTE: guest入室でinvitedVideoRoomが存在しない場合には、meeting/guest/を再度リクエスト
    if (this.guestToken) {
      if (!this.guestInvitedVideoRoom) {
        this.$router.push({
          name: 'guestMeeting',
          query: {
            token: this.guestToken
          }
        })
        return
      }
    } else {
      // NOTE: 一連の異常系検出条件
      // VideoRoomMasterが未取得であれば取得
      // => VideoRoomが取得できなければ会議室一覧画面に強制遷移
      // => 自身が参加メンバーでなければ予約画面に強制遷移
      if (!this.videoRoomMaster) await this.apiGetVideoRoomMaster(this.videoRoomMasterId)
      if (this.videoRoomMaster) {
        await this.apiGetVideoRoom({
          videoRoomMasterId: this.videoRoomMasterId,
          id: this.videoRoomId
        })
      }
      if (!this.videoRoom) {
        this.$router.push({ name: 'meeting' })
        return
      } else if (!this.videoRoom.myMemberId) {
        this.$router.push({ name: 'showMeetingReservations', params: { videoRoomMasterId: this.videoRoomMasterId, id: this.videoRoomId } })
        return
      }
    }

    window.addEventListener('beforeunload', this.beforeunloadOnWindow)
    window.addEventListener('unload', this.unloadOnWindow)

    this.mediaDevices = await navigator.mediaDevices.enumerateDevices()

    this.displayName = this.guestToken ?
                       this.guestInvitedVideoRoom.myMember.displayName :
                       this.signedInUser.meta.displayName

    if (this.videoRoom.isWaitable()) {
      if (this.defaultEnableMic) this.enableMic = await this.settingMic()
      if (this.defaultEnableWebcam) this.enableWebCam = await this.settingWebCam()
      this.waitingRoomInterval = setInterval(async () => {
        const now = moment(new Date)
        const startAt = moment(this.videoRoom.startAt)
        if (startAt.diff(now) <= 1000) {
          this.mediaDevices = await navigator.mediaDevices.enumerateDevices()
          this.closeEnabledDevices({
            audio: true,
            video: true
          })
          this.waitingRoomModal = false
          if (this.videoRoom.roomLocked) {
            this.enterRoomPasswordModal = true
          } else {
            this.userConfigurationModal = true
          }
          clearInterval(this.waitingRoomInterval)
        } else {
          const diffDuration = moment.duration(startAt.diff(now))
          const minutes = `0${diffDuration.minutes()}`.slice(-2)
          const seconds = `0${diffDuration.seconds()}`.slice(-2)
          this.waitingTime = diffDuration.hours() ?
                             `${`0${diffDuration.hours()}`.slice(-2)}：${minutes}：${seconds}` :
                             `${minutes}：${seconds}`
        }
      }, 1000)
      this.mediaDevices = await navigator.mediaDevices.enumerateDevices()
      this.waitingRoomModal = true

    } else if (this.videoRoom.roomLocked) {
      this.enterRoomPasswordModal = true
    } else {
      this.userConfigurationModal = true
    }
    if (this.videoRoom.userNotificationId) this.apiPutUserNotificationToRead(this.videoRoom.userNotificationId)
  },
  beforeDestroy: function() {
    window.removeEventListener('beforeunload', this.beforeunloadOnWindow)
    window.removeEventListener('unload', this.unloadOnWindow)
    this.close()
    if (this.videoRoom.isWaitable()) {
      this.closeEnabledDevices({
        audio: true,
        video: true
      })
    }
  },
  watch: {
    frontPeerIds: function(newValue, oldValue) {
      if (oldValue.length != newValue.length || oldValue.filter(i => newValue.indexOf(i) == -1).length) {
        this.webRTCClient.controlConsumers()
      }
    },
    sendWhiteboardData: function(data) {
      this.webRTCClient.sendWhiteboardMessage(data)
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";
@import "@/styles/mediaquery";

.video-room-container {
  width: 100vw;
  min-height: 100vh;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.waiting-room-modal {
  .waiting-room-wrapper {
    padding: 0 20px;
    @include mq(md) {
      padding: 0 50px;
    }
    .waiting-time-wrapper {
      width: 100%;
      height: 100px;
      padding: 18px;
      margin-bottom: 15px;
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-around;
      background-color: $light-gray-color;
      font-size: 18px;
      font-weight: 600;

      .title {
        color: $secondary-color;
      }
    }

    .room-information-table {
      width: 100%;
      margin-bottom: 15px;

      th {
        font-weight: 600;
        width: 68px;
      }

      th, td {
        padding-bottom: 20px;
      }
    }
  }
}

.peers-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: normal;
  align-items: center;
  align-content: baseline;
  flex-direction: column;
  @include mq(md) {
    flex-direction: row;
    justify-content: space-around;
  }

  .peer-view {
    height: 300px;
    width: 85%;
    @include mq(md) {
      width: 380px;
      min-width: 330px;
      height: 280px;
      margin-right: 20px;
    }
    @include mq(lg) {
      width: 420px;
      height: 310px;
    }
  }

  .user-setting {
    width: 90%;
    margin: 25px 0 0;
    @include mq(md) {
      margin: auto;
      width: auto;
    }
    .user-setting-heading {
      margin-bottom: 20px;
    }
  }
}

.media-select {
  margin: -10px 0 20px 0;
}

.toast-notifications-wrapper {
  position: fixed;
  width: 100vw;
  height: 100vh;
  top:0;
  left: 0;
  padding: 20px 20px 0 20px;
  z-index: 1049;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  .toast-notification-content {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-weight: 600;
    color: $primary-color;
    .remain-meeting-time {
      color: #000;
      margin: 0 5px 0 3px;
    }
  }
}
</style>
